import { useTranslation } from "react-i18next";
import { GetAllCompanyDto, InvoiceDto } from "@/types/api/api-types";
import { createColumnHelper } from "@tanstack/react-table";
import FileIcon from "@/assets/icons/file.svg?react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import { formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import { InvoicePaymentStatusBadge } from "@/components/shared/badge/invoicePaymentStatus.badge";

const columnHelper = createColumnHelper<InvoiceDto>();

export const useInvoiceTableColumns = (
  displayDocument: (invoiceKey: string) => Promise<void>,
  company?: GetAllCompanyDto,
) => {
  const { t } = useTranslation("boClients");
  return [
    columnHelper.display({
      id: "invoice",
      header: t("invoicesInvoiceTable.columns.invoice"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          <FileIcon width={20} height={20} />
          <span className="text-sm font-medium text-gray-900">
            {t("invoicesInvoiceTable.invoiceName", { invoiceId: info.row.original.invoiceId })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "billingEntity",
      header: t("invoicesInvoiceTable.columns.billingEntity"),
      cell: (info) => (
        <span className="text-sm font-medium text-gray-900">
          {company?.billingEntities.find((el) => el.id === info.row.original.billingEntityId)
            ?.companyName || ""}
        </span>
      ),
    }),
    columnHelper.display({
      id: "createdAt",
      header: t("invoicesInvoiceTable.columns.createdAt"),
      cell: (info) => (
        <span className="text-sm font-medium text-gray-900">
          {formatDate(info.row.original.createdAt, "dd MMM yyyy", {
            locale: fr,
          })}
        </span>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: t("invoicesInvoiceTable.columns.status"),
      meta: `justify-center`,
      cell: (info) => <InvoicePaymentStatusBadge status={info.row.original.paymentStatus} />,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="flex flex-row items-center justify-center gap-3">
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() =>
              displayDocument(info.row.original?.invoicePDFKey || info.row.original.invoiceKey)
            }
          >
            <EyeIcon width={20} height={20} color="#06F" />
          </button>
        </div>
      ),
    }),
  ];
};
